/**
 * Web Vitals Performance Monitoring
 * 
 * Tracks Core Web Vitals metrics and sends them to analytics.
 * 
 * Metrics tracked:
 * - LCP (Largest Contentful Paint): Loading performance
 * - INP (Interaction to Next Paint): Interactivity (replaces FID)
 * - CLS (Cumulative Layout Shift): Visual stability
 * - FCP (First Contentful Paint): Initial load
 * - TTFB (Time to First Byte): Server response
 */

import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';

/**
 * Send metric to analytics
 */
function sendToAnalytics(metric: Metric) {
  const { name, value, id, navigationType } = metric;

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Web Vitals] ${name}:`, {
      value: Math.round(value),
      id,
      navigationType,
      rating: getRating(name, value),
    });
  }

  // Send to PostHog (if available)
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture('web_vitals', {
      metric_name: name,
      metric_value: value,
      metric_id: id,
      metric_rating: getRating(name, value),
      navigation_type: navigationType,
    });
  }

  // Send to Sentry Performance (if available)
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.metrics.distribution(name, value, {
      tags: {
        rating: getRating(name, value),
        navigation_type: navigationType,
      },
      unit: 'millisecond',
    });
  }
}

/**
 * Get performance rating (Good/Needs Improvement/Poor)
 * Based on Google's Core Web Vitals thresholds
 */
function getRating(metricName: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds: Record<string, { good: number; poor: number }> = {
    LCP: { good: 2500, poor: 4000 },  // ms
    INP: { good: 200, poor: 500 },    // ms (replaces FID)
    CLS: { good: 0.1, poor: 0.25 },   // score
    FCP: { good: 1800, poor: 3000 },  // ms
    TTFB: { good: 800, poor: 1800 },  // ms
  };

  const threshold = thresholds[metricName];
  if (!threshold) return 'good';

  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

/**
 * Initialize Web Vitals monitoring
 * Call this once when the app loads
 */
export function initWebVitals() {
  // Only run in browser
  if (typeof window === 'undefined') return;

  // Track Core Web Vitals
  onCLS(sendToAnalytics);
  onINP(sendToAnalytics);  // Replaced FID with INP (March 2024)
  onLCP(sendToAnalytics);
  onFCP(sendToAnalytics);
  onTTFB(sendToAnalytics);

  console.log('[Web Vitals] ✅ Performance monitoring initialized');
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    posthog?: {
      capture: (event: string, properties: Record<string, unknown>) => void;
    };
    Sentry?: {
      metrics: {
        distribution: (
          name: string,
          value: number,
          options: { tags: Record<string, string>; unit: string }
        ) => void;
      };
    };
  }
}
